import jsPDF from 'jspdf';

let fontBase64Cache = null;

const loadFontAsBase64 = async () => {
  if (fontBase64Cache) return fontBase64Cache;
  try {
    const fontUrl = `${window.location.origin}/NotoSansTC-Regular.ttf`;
    const response = await fetch(fontUrl);
    if (!response.ok) throw new Error(`Font fetch failed: ${response.status}`);
    const buffer = await response.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    fontBase64Cache = btoa(binary);
    return fontBase64Cache;
  } catch (error) {
    console.error('Failed to load font:', error);
    return null;
  }
};

const registerChineseFont = async (doc) => {
  try {
    const base64 = await loadFontAsBase64();
    if (!base64) return false;
    doc.addFileToVFS('NotoSansTC-Regular.ttf', base64);
    doc.addFont('NotoSansTC-Regular.ttf', 'NotoSansTC', 'normal');
    return true;
  } catch (error) {
    console.error('Failed to register Chinese font:', error);
    return false;
  }
};

const loadLogo = async () => {
  return new Promise((resolve) => {
    const logoUrl = `${window.location.origin}/Report_logo.png`;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = logoUrl;
  });
};

const setChinese = (doc, available) => {
  if (available) doc.setFont('NotoSansTC', 'normal');
};

export const generateEScoreCardPDF = async (inspectionData, annotationImageBase64) => {
  const {
    contractNo, make, model, carInDate, licenseNo,
    cylinderCapacity, year, mileage, numberOfOwners,
    exteriorScore, interiorGrade, details
  } = inspectionData;

  const logoDataUrl = await loadLogo();
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const cn = await registerChineseFont(doc);

  const pageWidth = 210;
  const margin = 15;
  const contentWidth = pageWidth - (margin * 2);
  const primaryColor = [44, 62, 80];
  const blueColor = [52, 152, 219];
  const textColor = [44, 62, 80];
  const borderColor = [189, 195, 199];

  let yPos = 15;

  // === HEADER ===
  if (logoDataUrl) {
    try { doc.addImage(logoDataUrl, 'PNG', margin, yPos, 70, 25); }
    catch (e) { /* skip */ }
  }

  // Title - moved left (centered between logo end and page edge)
  const titleX = pageWidth - margin - 65;
  setChinese(doc, cn);
  doc.setFontSize(18);
  doc.setTextColor(...textColor);
  doc.text('車輛狀態評價書', titleX, yPos + 10);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  doc.text('Vehicle Grading Report', titleX, yPos + 20);

  yPos = 50;

  // === VEHICLE INFORMATION ===
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...primaryColor);
  doc.text('Vehicle Information', margin, yPos);
  yPos += 10;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...textColor);

  // Centered vehicle info with equal left/right spacing
  const infoIndent = 10;
  const col1X = margin + infoIndent;
  const col2X = margin + contentWidth / 2 + infoIndent;
  const labelWidth = 38;
  const rowHeight = 7;

  const vehicleInfo = [
    [['Make:', String(make || '-')], ['# of Owners:', String(numberOfOwners || '-')]],
    [['Manu. year:', String(year || '-')], ['Model:', String(model || '-')]],
    [['Cylinder Capacity:', String(cylinderCapacity || '-')], ['Mileage:', String(mileage || '-')]],
    [['Car-In Date:', carInDate ? new Date(carInDate).toLocaleDateString() : '-'], ['License No.:', String(licenseNo || '-')]],
    [['Contract No.:', String(contractNo || '-')], ['Generated:', new Date().toLocaleDateString()]]
  ];

  vehicleInfo.forEach((row) => {
    if (row[0]) { doc.text(row[0][0], col1X, yPos); doc.text(row[0][1], col1X + labelWidth, yPos); }
    if (row[1]) { doc.text(row[1][0], col2X, yPos); doc.text(row[1][1], col2X + labelWidth, yPos); }
    yPos += rowHeight;
  });
  yPos += 10;

  // === ASSESSMENT RESULTS ===
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...primaryColor);
  doc.text('Assessment Results', margin, yPos);
  yPos += 10;

  const boxWidth = 50, boxHeight = 35, boxSpacing = 20;
  const startX = margin + (contentWidth - (boxWidth * 2 + boxSpacing)) / 2;

  doc.setDrawColor(...borderColor);
  doc.setLineWidth(0.5);
  doc.roundedRect(startX, yPos, boxWidth, boxHeight, 3, 3, 'S');

  setChinese(doc, cn);
  doc.setFontSize(10);
  doc.setTextColor(...textColor);
  doc.text('車身 Exterior', startX + boxWidth / 2, yPos + 8, { align: 'center' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(...blueColor);
  doc.text(String(exteriorScore || '0.00'), startX + boxWidth / 2, yPos + 24, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...textColor);
  doc.text('/ 5', startX + boxWidth / 2 + 12, yPos + 26);

  const interiorX = startX + boxWidth + boxSpacing;
  doc.roundedRect(interiorX, yPos, boxWidth, boxHeight, 3, 3, 'S');

  setChinese(doc, cn);
  doc.setFontSize(10);
  doc.setTextColor(...textColor);
  doc.text('內裝 Interior', interiorX + boxWidth / 2, yPos + 8, { align: 'center' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(...blueColor);
  doc.text(String(interiorGrade || '-'), interiorX + boxWidth / 2, yPos + 24, { align: 'center' });

  yPos += boxHeight + 15;

  // === DAMAGE ANNOTATION IMAGE ===
  if (annotationImageBase64) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...primaryColor);
    doc.text('Damage Annotation Image', margin, yPos);
    yPos += 8;

    try {
      const maxWidth = contentWidth * 0.6;
      const maxHeight = 100;
      let finalWidth = maxWidth;
      let finalHeight = maxWidth * (570 / 386);
      if (finalHeight > maxHeight) { finalHeight = maxHeight; finalWidth = maxHeight * (386 / 570); }

      const imgX = margin + (contentWidth - finalWidth) / 2;
      doc.setDrawColor(...borderColor);
      doc.rect(imgX - 1, yPos - 1, finalWidth + 2, finalHeight + 2, 'S');
      doc.addImage(annotationImageBase64, 'PNG', imgX, yPos, finalWidth, finalHeight);
      yPos += finalHeight + 10;
    } catch (error) {
      console.error('Failed to add annotation image:', error);
      yPos += 10;
    }
  }

  // === PAGE 2: DAMAGE ASSESSMENT DETAILS ===
  doc.addPage();
  yPos = 20;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...primaryColor);
  doc.text('Damage Assessment Details', margin, yPos);
  yPos += 10;

  let detailsArray = [];
  if (details) {
    if (Array.isArray(details)) detailsArray = details;
    else if (typeof details === 'object') detailsArray = Object.values(details);
  }

  const damageItems = detailsArray.filter(d => d.count > 0);

  if (damageItems.length > 0) {
    // Sort by categoryId for proper grouping
    damageItems.sort((a, b) => (a.categoryId || 0) - (b.categoryId || 0));

    // Calculate category totals (capped at 0.33)
    const categoryTotals = {};
    damageItems.forEach(item => {
      const cat = item.category || '-';
      if (!categoryTotals[cat]) categoryTotals[cat] = 0;
      categoryTotals[cat] += (item.scoreDeduction || 0);
    });
    // Cap each at 0.33
    Object.keys(categoryTotals).forEach(cat => {
      categoryTotals[cat] = Math.min(categoryTotals[cat], 0.33);
    });

    // Count items per category for row merging
    const categoryCounts = {};
    const categoryFirstRow = {};
    damageItems.forEach((item, idx) => {
      const cat = item.category || '-';
      if (!categoryCounts[cat]) { categoryCounts[cat] = 0; categoryFirstRow[cat] = idx; }
      categoryCounts[cat]++;
    });

    // Table setup
    const colWidths = [28, 18, 52, 16, 30, 35]; // Category, Code, Description, Count, Score Deduction, Category Total
    const tableWidth = colWidths.reduce((a, b) => a + b, 0);
    const tableX = margin + (contentWidth - tableWidth) / 2;
    const headerHeight = 8;
    const dataRowHeight = 8;

    // Header - blue background, white bold text, white cell dividers
    doc.setFillColor(...blueColor);
    doc.rect(tableX, yPos, tableWidth, headerHeight, 'F');

    // Draw white vertical lines between header cells
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.3);
    let divX = tableX;
    colWidths.forEach((w, i) => {
      divX += w;
      if (i < colWidths.length - 1) {
        doc.line(divX, yPos, divX, yPos + headerHeight);
      }
    });

    // Draw outer border for header
    doc.setDrawColor(...blueColor);
    doc.setLineWidth(0.3);
    doc.rect(tableX, yPos, tableWidth, headerHeight, 'S');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(255, 255, 255);

    const headers = ['Category', 'Code', 'Description', 'Count', 'Score Deduction', 'Category Total'];
    let currentX = tableX + 2;
    headers.forEach((h, i) => { doc.text(h, currentX, yPos + 5.5); currentX += colWidths[i]; });
    yPos += headerHeight;

    // Data rows - blue borders, no fill, normal weight
    doc.setLineWidth(0.3);

    damageItems.forEach((item, index) => {
      const cat = item.category || '-';
      const isFirstInCategory = categoryFirstRow[cat] === index;
      const rowSpan = categoryCounts[cat];

      doc.setDrawColor(...blueColor);

      // Draw cell borders for each column
      let cellX = tableX;
      colWidths.forEach((w, colIdx) => {
        if (colIdx === 0 || colIdx === 5) {
          // Category and Category Total columns - only draw on first row of category
          if (isFirstInCategory) {
            doc.rect(cellX, yPos, w, dataRowHeight * rowSpan, 'S');
          }
        } else {
          doc.rect(cellX, yPos, w, dataRowHeight, 'S');
        }
        cellX += w;
      });

      doc.setTextColor(...textColor);
      currentX = tableX + 2;

      // Category (Chinese) - only on first row of category
      if (isFirstInCategory) {
        setChinese(doc, cn);
        doc.setFontSize(7);
        const catYCenter = yPos + (dataRowHeight * rowSpan) / 2 + 1.5;
        doc.text(String(cat), currentX, catYCenter);
      }
      currentX += colWidths[0];

      // Code
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(String(item.damageCode || '-'), currentX, yPos + 5.5);
      currentX += colWidths[1];

      // Description (Chinese)
      setChinese(doc, cn);
      doc.setFontSize(7);
      doc.text(String(item.description || '-'), currentX, yPos + 5.5);
      currentX += colWidths[2];

      // Count
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(String(item.count || 0), currentX, yPos + 5.5);
      currentX += colWidths[3];

      // Score Deduction
      doc.text(String((item.scoreDeduction || 0).toFixed(2)), currentX, yPos + 5.5);
      currentX += colWidths[4];

      // Category Total - only on first row of category
      if (isFirstInCategory) {
        const total = categoryTotals[cat];
        const totalText = total.toFixed(2);
        const catYCenter = yPos + (dataRowHeight * rowSpan) / 2 + 1.5;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        if (total >= 0.33) {
          doc.text(totalText + ' (MAX)', currentX, catYCenter);
        } else {
          doc.text(totalText, currentX, catYCenter);
        }
      }

      yPos += dataRowHeight;
    });
  } else {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(127, 140, 141);
    doc.text('No damage records found', margin, yPos);
  }

  // Add page numbers to all pages
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, 290, { align: 'center' });
  }

  const pdfBlob = doc.output('blob');
  console.log('PDF generated, size:', pdfBlob.size, 'bytes');
  return pdfBlob;
};

export const pdfService = { generateEScoreCardPDF };
export default pdfService;
